import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, shareReplay, switchMap } from 'rxjs/operators';
import { SiteCreateMessage } from './site-create';
import { SiteApiService } from './site.apiservice';
import { Site, SiteStatus, SiteUpdate } from './site';
import { Job } from '../job';
import { SiteCreateResponseV2 } from './apisite-create';
import { ApiSiteMigrateResponse } from './apisite-migrate';
import { Domains } from './domain';
import { SelectedSiteService } from '../../selected-site.service';
import { SiteMigrateMessage } from './site-migrate';
@Injectable()
export class SiteService {
  public cachedCreateMessage: SiteCreateMessage;
  public cachedMigrateMessage: SiteMigrateMessage;

  siteId: string;
  site$: Observable<Site>;
  public pendingDomainsStat: { [key: string]: boolean };

  constructor(
    private api: SiteApiService,
    private selectedSiteService: SelectedSiteService,
  ) {
    this.cachedCreateMessage = null;

    this.site$ = this.selectedSiteService.getSiteId().pipe(
      filter((siteId: string) => !!siteId),
      switchMap((siteId: string) => {
        this.siteId = siteId;
        return this.api.getV2(siteId);
      }),
      shareReplay(1),
    );

    this.pendingDomainsStat = {};
  }

  hasCachedCreateMessage(): boolean {
    return this.cachedCreateMessage !== null;
  }

  getPendingDomainsStat(): any {
    return this.pendingDomainsStat;
  }

  setPendingDomainsStat(domain: string, flag: boolean): void {
    this.pendingDomainsStat[domain] = flag;
  }

  get(): Observable<Site> {
    return this.site$;
  }

  lookup(accountId: string): Observable<Site> {
    return this.api.lookup(accountId);
  }

  lookupAll(partnerId: string, cursor: string = null): Observable<{ sites: Site[]; cursor: string }> {
    let result = null;

    if (!cursor) {
      result = this.api.lookupAll(partnerId);
    } else {
      result = this.api.lookupAll(partnerId, cursor);
    }

    return result;
  }

  changePrimaryDomain(siteId: string, domain: string): Observable<Site> {
    return this.api.changePrimaryDomain(siteId, domain);
  }

  getJobForSite(siteId: string, jobId: string): Observable<Job> {
    return this.api.getJobForSite(siteId, jobId);
  }

  createAsync(site: SiteCreateMessage, user_id?: string): Observable<SiteCreateResponseV2> {
    this.cachedCreateMessage = site;
    return this.api.createV2(site, user_id);
  }
  migrateAsync(site: SiteMigrateMessage, user_id?: string): Observable<ApiSiteMigrateResponse> {
    this.cachedMigrateMessage = site;
    return this.api.apiMigrate(site, user_id);
  }

  // Get domains associated with a site
  getAssociatedDomains(siteId: string): Observable<Domains> {
    return this.api.getAssociatedDomainsV2(siteId);
  }

  deleteDomainV2(siteId: string, domain: string): Observable<boolean> {
    return this.api.deleteDomainV2(siteId, domain);
  }

  addDomainV3(siteId: string, domains: string[]): Observable<boolean> {
    return this.api.addDomainV3(siteId, domains);
  }

  getStatus(siteId: string): Observable<SiteStatus> {
    return this.api.getStatus(siteId);
  }

  getSiteUpdate(domain: string): Observable<SiteUpdate> {
    return this.api.getSiteUpdate(domain);
  }

  getSkipHTTPSUpgradeFlag(): Observable<boolean> {
    return this.api.getSkipHTTPSUpgradeFlag(this.siteId);
  }

  setSkipHTTPSUpgradeFlag(skipUpgradeFlag: boolean): Observable<boolean> {
    return this.api.setSkipHTTPSUpgradeFlag(this.siteId, skipUpgradeFlag);
  }

  getWPLoginExposedFlag(): Observable<boolean> {
    return this.api.getWPLoginExposedFlag(this.siteId);
  }

  setWPLoginExposedFlag(exposeWPLoginFlag: boolean): Observable<boolean> {
    return this.api.setWPLoginExposedFlag(this.siteId, exposeWPLoginFlag);
  }

  getGAState(): Observable<boolean> {
    return this.api.getGAState(this.siteId);
  }

  setGAState(gaState: boolean): Observable<boolean> {
    return this.api.setGAState(this.siteId, gaState);
  }
}
